import { Injectable, inject, signal, computed } from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from 'firebase/functions';
import { AuthService } from './auth.service';

interface CallableSuccess<T> { success: true; data: T }

type FirestoreTimestamp = { toMillis(): number };

@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly functions = inject(Functions);
  private readonly authService = inject(AuthService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Derived billing state — reads from AuthService.userProfile() which already holds a
  // live Firestore listener on users/{uid}. No second listener opened.
  readonly subscriptionStatus = computed(
    () => this.authService.userProfile()?.subscriptionStatus ?? 'none'
  );
  readonly billingType = computed(
    () => this.authService.userProfile()?.billingType ?? 'self'
  );

  readonly hasActiveAccess = computed(() => {
    const profile = this.authService.userProfile();
    if (!profile) return false;

    const { subscriptionStatus, billingType, trialEndsAt, gracePeriodEndsAt } = profile;
    const now = Date.now();

    if (billingType === 'coach-covered') return true;
    if (subscriptionStatus === 'active') return true;
    if (subscriptionStatus === 'past_due') return true;
    if (subscriptionStatus === 'trialing') {
      const ts = trialEndsAt as FirestoreTimestamp | null;
      return ts ? ts.toMillis() > now : false;
    }
    if (subscriptionStatus === 'cancelled') {
      const ts = gracePeriodEndsAt as FirestoreTimestamp | null;
      return ts ? ts.toMillis() > now : false;
    }
    return false;
  });

  // Callables initialized once to avoid re-wrapping the Functions instance on every call
  private readonly updateSeatsCallable = httpsCallable<
    { athleteUid: string; coverSubscription: boolean },
    CallableSuccess<undefined>
  >(this.functions, 'updateCoachSeats');

  private readonly generateInviteCallable = httpsCallable<
    Record<string, never>,
    CallableSuccess<{ inviteCode: string; inviteUrl: string }>
  >(this.functions, 'generateInviteCode');

  private readonly acceptInviteCallable = httpsCallable<
    { inviteCode: string },
    CallableSuccess<{ coachUid: string; coachName: string | null }>
  >(this.functions, 'acceptCoachInvite');

  private readonly disconnectCallable = httpsCallable<
    Record<string, never>,
    CallableSuccess<undefined>
  >(this.functions, 'disconnectFromCoach');

  private readonly checkoutCallable = httpsCallable<
    { priceId: string; successUrl: string; cancelUrl: string },
    CallableSuccess<{ url: string }>
  >(this.functions, 'createCheckoutSession');

  private readonly portalCallable = httpsCallable<
    Record<string, never>,
    CallableSuccess<{ url: string }>
  >(this.functions, 'createPortalSession');

  private readonly upgradeToCoachCallable = httpsCallable<
    { priceId: string },
    CallableSuccess<undefined>
  >(this.functions, 'upgradeToCoach');

  private readonly downgradeToAthleteCallable = httpsCallable<
    { priceId: string; confirmed: true; successUrl: string; cancelUrl: string },
    CallableSuccess<{ checkoutUrl: string }>
  >(this.functions, 'downgradeToAthlete');

  async createCheckoutSession(priceId: string, successUrl: string, cancelUrl: string): Promise<string> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.checkoutCallable({ priceId, successUrl, cancelUrl });
      return result.data.data!.url;
    } catch {
      this.error.set('billing.errors.checkoutFailed');
      throw new Error('billing.errors.checkoutFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async updateCoachSeats(athleteUid: string, coverSubscription: boolean): Promise<void> {
    this.error.set(null);
    try {
      await this.updateSeatsCallable({ athleteUid, coverSubscription });
    } catch {
      this.error.set('billing.errors.updateSeatsFailed');
      throw new Error('billing.errors.updateSeatsFailed');
    }
  }

  async generateInviteCode(): Promise<{ inviteCode: string; inviteUrl: string }> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.generateInviteCallable({});
      return result.data.data!;
    } catch {
      this.error.set('billing.errors.generateInviteFailed');
      throw new Error('billing.errors.generateInviteFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async acceptCoachInvite(inviteCode: string): Promise<{ coachUid: string; coachName: string | null }> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.acceptInviteCallable({ inviteCode });
      return result.data.data!;
    } catch {
      this.error.set('billing.errors.acceptInviteFailed');
      throw new Error('billing.errors.acceptInviteFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async disconnectFromCoach(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await this.disconnectCallable({});
    } catch {
      this.error.set('billing.errors.disconnectFailed');
      throw new Error('billing.errors.disconnectFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async upgradeToCoach(priceId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      await this.upgradeToCoachCallable({ priceId });
    } catch {
      this.error.set('billing.errors.upgradeFailed');
      throw new Error('billing.errors.upgradeFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async downgradeToAthlete(priceId: string, successUrl: string, cancelUrl: string): Promise<string> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.downgradeToAthleteCallable({ priceId, confirmed: true, successUrl, cancelUrl });
      return result.data.data!.checkoutUrl;
    } catch {
      this.error.set('billing.errors.downgradeFailed');
      throw new Error('billing.errors.downgradeFailed');
    } finally {
      this.loading.set(false);
    }
  }

  async createPortalSession(): Promise<string> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const result = await this.portalCallable({});
      return result.data.data!.url;
    } catch {
      this.error.set('billing.errors.portalFailed');
      throw new Error('billing.errors.portalFailed');
    } finally {
      this.loading.set(false);
    }
  }
}
